import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  resolveLibraryItemUserVisibleLabel,
  resolveLibraryItemStatusClassName,
} from "./cognitoryLibraryIngestionStatus";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
} from "../theme/mainPageVisual";

function formatUploadedAtLabel(versionCreatedAt: string | null): string | null {
  if (!versionCreatedAt) {
    return null;
  }
  const parsed = Date.parse(versionCreatedAt);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return new Date(parsed).toLocaleString();
}

function formatContentTypeLabel(contentType: string | null): string {
  if (!contentType) {
    return "unknown type";
  }
  if (contentType === "application/pdf") {
    return "PDF";
  }
  return contentType;
}

export function CognitoryLibraryDocumentRow(input: {
  document: GovernedLibraryDocumentSummary;
  timedOut: boolean;
  modalBlocksLibrary: boolean;
  openingDocumentId: string | null;
  onView: (document: GovernedLibraryDocumentSummary) => void;
  onDelete: (
    document: GovernedLibraryDocumentSummary,
    trigger: HTMLButtonElement | null,
  ) => void;
}) {
  const { document, timedOut } = input;
  const userVisibleLabel = resolveLibraryItemUserVisibleLabel({
    canonicalState: document.canonicalState,
    versionStatus: document.versionStatus,
    timedOut,
  });
  const uploadedAtLabel = formatUploadedAtLabel(document.versionCreatedAt);
  const metadataParts = [
    document.filename ?? "No filename",
    formatContentTypeLabel(document.contentType),
    uploadedAtLabel ? `Uploaded ${uploadedAtLabel}` : null,
  ].filter((part): part is string => Boolean(part));

  const viewDisabled =
    input.modalBlocksLibrary ||
    !document.governedSourceRecorded ||
    input.openingDocumentId === document.documentId;

  return (
    <li
      data-testid="cognitory-library-item"
      data-document-id={document.documentId}
      data-version-status={document.versionStatus ?? ""}
      data-canonical-state={document.canonicalState ?? ""}
      className="cognitory-library-item cognitory-library-item--compact"
      style={{
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: MAIN_PAGE_COLORS.panelFill,
        fontFamily: MAIN_PAGE_FONT_STACK,
      }}
    >
      <div className="cognitory-library-item-row">
        <div className="cognitory-library-item-content">
          <p
            data-testid="cognitory-library-item-title"
            className="cognitory-library-item-title"
            style={{ color: MAIN_PAGE_COLORS.inkStrong }}
          >
            {document.title}
          </p>
          <p
            className="cognitory-library-item-meta-line"
            style={{ color: MAIN_PAGE_COLORS.muted }}
          >
            {metadataParts.join(" · ")}
          </p>
        </div>

        <span
          data-testid="cognitory-library-item-status"
          className={`cognitory-library-item-badge ${resolveLibraryItemStatusClassName(
            {
              canonicalState: document.canonicalState,
              versionStatus: document.versionStatus,
              timedOut,
            },
          )}`}
        >
          {userVisibleLabel}
        </span>

        <div className="cognitory-library-item-actions cognitory-library-item-actions--compact">
          <button
            type="button"
            data-testid="cognitory-library-view"
            data-document-id={document.documentId}
            className="cognitory-library-row-button"
            disabled={viewDisabled}
            onClick={() => input.onView(document)}
          >
            {input.openingDocumentId === document.documentId
              ? "Opening…"
              : "View"}
          </button>
          <button
            type="button"
            data-testid="cognitory-library-delete"
            data-document-id={document.documentId}
            className="cognitory-library-row-button cognitory-library-row-button--danger"
            disabled={input.modalBlocksLibrary}
            onClick={event => input.onDelete(document, event.currentTarget)}
          >
            Delete
          </button>
        </div>
      </div>
    </li>
  );
}
